import { useEffect, useState } from 'react';
import { forwardRef } from 'react';
import { useFormik } from 'formik';

import onboardingValidationSchemas from '../../../../constants/schemas/onboarding';
import CustomDropdown from '../../../../components/base/custom-dropdown';
import {
  useOnboardingStore,
  useRateStore,
  useResourceStore,
} from '../../../../store';
import AppInput from '../../../../components/base/input';
import GoBack from '../../../../components/base/go-back';
import { ICountryState } from '../../../../models/resource.model';
import { IActiveCountry } from '../../../../models/rate.model';

export const BusinessAddress = forwardRef(() => {
  const { setBusinessAddress, businessAddress } = useOnboardingStore();

  const {
    getAllCountryStates,
    countryStates,
    loading: loadingResources,
  } = useResourceStore();
  const { activeCountries: countries } = useRateStore();
  const filteredCountries = countries.filter(
    (country) =>
      country.name === 'Nigeria' ||
      country.name === 'Ghana' ||
      country.name === 'Senegal' ||
      country.name === 'Cameroon',
  );

  const formik: any = useFormik({
    initialValues: {
      streetLine1: businessAddress.streetLine1,
      streetLine2: businessAddress.streetLine2,
      city: businessAddress.city,
      state: businessAddress.state,
      postalCode: businessAddress.postalCode,
      countryCode: businessAddress.countryCode,
      countryName: businessAddress.countryName,
      countryId: businessAddress.countryId,
    },
    validationSchema: onboardingValidationSchemas[1],
    validateOnMount: true,
    async onSubmit() {},
  });

  const [countryTouched, setCountryTouched] = useState(false);
  const handleCountryChange = (value: string) => {
    formik.values.countryCode = value;
    handleStateChange('');
    getAllCountryStates(formik.values.countryCode);
  };

  const [stateTouched, setStateTouched] = useState(false);
  const handleStateChange = (value: string) => {
    formik.values.state = value;
  };

  useEffect(() => {
    setBusinessAddress(formik.values);
  }, [
    formik.values,
    formik.values.countryCode,
    formik.values.countryId,
    formik.values.state,
  ]);

  return (
    <form onSubmit={formik.handleSubmit} action="#">
      {/* Title */}
      <div>
        <GoBack text="Go back" path="/start-kyb" />

        <h2 className="text-2xl font-bold text-[#111827]">
          Tell us about your business location
        </h2>
        <p className="text-gray-text text-sm">
          This will be used as your default business address. You can change it
          later
        </p>
      </div>

      {/* Form Fields */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8 border rounded-xl p-8">
        {/* Country */}
        <div>
          <CustomDropdown
            label="Select Country"
            options={filteredCountries.map((country: IActiveCountry) => {
              return {
                name: country.iso3,
                label: country.name,
                countryFlag: country.flagUrl,
                id: country.id,
              };
            })}
            selectedValue={formik.values.countryCode}
            onSelectChange={(value) => {
              formik.values.countryName = value.label;
              formik.values.countryId = value.id;
              handleCountryChange(value.name);
              setCountryTouched(true);
            }}
            emitObject
            required
            showCountryFlag
            errorMessage={
              !formik.values.countryCode && countryTouched
                ? 'Country is required'
                : ''
            }
          />
        </div>

        {/* State */}
        <div>
          <CustomDropdown
            label="Select State"
            options={countryStates.map((state: ICountryState) => {
              return {
                name: state.code,
                label: state.name,
              };
            })}
            selectedValue={formik.values.state}
            loading={loadingResources}
            onSelectChange={(value) => {
              handleStateChange(value);
              setStateTouched(true);
            }}
            required
            errorMessage={
              !formik.values.state && stateTouched ? 'State is required' : ''
            }
          />
        </div>

        {/* City */}
        <div>
          <AppInput
            type="text"
            name="city"
            id="city"
            onChange={formik.handleChange}
            placeholder="Enter city"
            required
            label="City"
            onBlur={formik.handleBlur}
            value={formik.values.city}
            errorMessage={
              formik.touched.city && formik.errors.city
                ? formik.errors.city
                : ''
            }
          />
        </div>

        {/* Postal Code */}
        <div>
          <AppInput
            type="text"
            name="postalCode"
            id="postalCode"
            onChange={formik.handleChange}
            placeholder="Enter postal code"
            label="Postal Code"
            required
            onBlur={formik.handleBlur}
            value={formik.values.postalCode}
            errorMessage={
              formik.touched.postalCode && formik.errors.postalCode
                ? formik.errors.postalCode
                : ''
            }
          />
        </div>

        {/* Address Line 1, Address Line 2 */}
        <div className="col-span-2">
          <AppInput
            type="text"
            name="streetLine1"
            id="streetLine1"
            onChange={formik.handleChange}
            placeholder="Enter address line 1"
            required
            label="Address 1"
            onBlur={formik.handleBlur}
            value={formik.values.streetLine1}
            errorMessage={
              formik.touched.streetLine1 && formik.errors.streetLine1
                ? formik.errors.streetLine1
                : ''
            }
          />
        </div>

        <div className="col-span-2">
          <AppInput
            type="text"
            name="streetLine2"
            id="streetLine2"
            onChange={formik.handleChange}
            placeholder="Enter address line 2"
            label="Address 2"
          />
        </div>
      </div>
    </form>
  );
});

// Add displayName for debugging
BusinessAddress.displayName = 'BusinessInfo';

export default BusinessAddress;
